// Help Assistant — question matching v1 (words + synonyms, English + Hebrew).
// Scores a typed question against every library entry: its question (EN + HE), every alias users taught it
// ("this helped"), and its written steps (weaker). Pure functions; no DOM, no network.

const STOP = new Set(('how do i a an the to my can is in of for on and it what if with me we you our be does there way this that ' +
  'from at are am or so any get who didnt dont cant isnt much many nobody unmarked yet').split(' ')
  .concat('איך את של על עם מה אם זה לי אני יש כדי אפשר מי לא'.split(' ')));

const SYN = [
  ['appointment', 'session', 'meeting', 'booking', 'book', 'appt', 'slot', 'מפגש', 'פגישה', 'תור', 'טיפול'],
  ['homework', 'exercise', 'practice', 'practise', 'drill', 'תרגול', 'תרגיל', 'שיעורי'],
  ['note', 'summary', 'writeup', 'סיכום'],
  ['delete', 'remove', 'erase', 'מוחקים', 'מחיקה'],
  ['cancel', 'noshow', 'missed', 'come', 'skip', 'ביטול', 'בוטל', 'מבטלים'],
  ['child', 'kid', 'son', 'daughter', 'patient', 'client', 'ילד', 'ילדה', 'מטופל', 'ילדים'],
  ['message', 'chat', 'text', 'contact', 'tell', 'inform', 'הודעה', 'צאט', 'צ׳אט'],
  ['paid', 'payment', 'invoice', 'receipt', 'billing', 'money', 'תשלום', 'שולם', 'קבלה'],
  ['price', 'prices', 'cost', 'rate', 'fee', 'מחיר', 'מחירים'],
  ['payout', 'pay', 'salary', 'split', 'wage', 'earn', 'שכר'],
  ['therapist', 'specialist', 'clinician', 'ot', 'slp', 'מטפל', 'מטפלת'],
  ['calendar', 'diary', 'schedule', 'יומן'],
  ['file', 'document', 'pdf', 'attachment', 'letter', 'upload', 'קובץ', 'מסמך', 'מעלים'],
  ['report', 'דוח', 'דוחות'],
  ['language', 'hebrew', 'english', 'שפה', 'עברית'],
  ['invite', 'join', 'access', 'share', 'onboard', 'hire', 'מזמינים'],
  ['change', 'edit', 'move', 'reschedule', 'update', 'משנים', 'עורכים'],
  ['see', 'view', 'find', 'show', 'look', 'where', 'check', 'רואים', 'מוצאים'],
  ['remind', 'reminder', 'alert', 'notification', 'notify', 'תזכורת', 'התראות'],
  ['add', 'create', 'new', 'make', 'מוסיפים'],
  ['week', 'weekly', 'recurring', 'repeat', 'series', 'שבועית'],
  ['parent', 'mom', 'mother', 'dad', 'father', 'family', 'הורה', 'משפחה'],
  ['colleague', 'team', 'coworker', 'עמית', 'צוות'],
  ['log', 'mark', 'record', 'tick', 'מסמנים'],
  ['attendance', 'attended', 'present', 'absent', 'נוכחות'],
];
const SYNMAP = new Map();
SYN.forEach(g => g.forEach(w => SYNMAP.set(w, g[0])));
const GENERIC = new Set(['add', 'see', 'change']); // verbs that appear everywhere count for less

function norm(tok) {
  if (/[֐-׿]/.test(tok)) {
    tok = tok.replace(/[֑-ׇ]/g, '');
    if (SYNMAP.has(tok)) return SYNMAP.get(tok);
    if (tok.length > 3 && 'הובלמשכ'.includes(tok[0])) { const t = tok.slice(1); return SYNMAP.get(t) || t; }
    return tok;
  }
  if (SYNMAP.has(tok)) return SYNMAP.get(tok);
  let t = tok;
  if (t.length > 5 && t.endsWith('ing')) t = t.slice(0, -3);
  else if (t.length > 4 && t.endsWith('ed')) t = t.slice(0, -2);
  else if (t.length > 3 && t.endsWith('es') && !t.endsWith('ses')) t = t.slice(0, -2);
  else if (t.length > 3 && t.endsWith('s')) t = t.slice(0, -1);
  return SYNMAP.get(t) || t;
}

export function keys(text) {
  return [...new Set(String(text || '').toLowerCase().replace(/[’']/g, '').replace(/[^\p{L}\p{N}\s׳-]/gu, ' ')
    .split(/\s+/).filter(w => w && !STOP.has(w)).map(norm).filter(w => w.length > 1))];
}

export function buildIndex(library) {
  const df = new Map();
  for (const e of library) {
    const phrases = [e.q?.en, e.q?.he, ...(e.aliases || [])].filter(Boolean).map(p => new Set(keys(p)));
    const flat = arr => (arr || []).flatMap(x => (x && typeof x === 'object' ? [x.title, ...(x.steps || [])] : [x]));
    const steps = new Set(keys([...flat(e.steps?.en), ...flat(e.steps?.he)].join(' ')));
    e._k = { phrases, steps };
    new Set([...phrases.flatMap(p => [...p]), ...steps]).forEach(k => df.set(k, (df.get(k) || 0) + 1));
  }
  const N = library.length || 1;
  return { library, idf: new Map([...df].map(([k, v]) => [k, Math.log(1 + N / v)])), N };
}

export function score(index, query, e) {
  const Q = keys(query); if (!Q.length) return 0;
  const w = k => (index.idf.get(k) || Math.log(1 + index.N)) * (GENERIC.has(k) ? 0.5 : 1);
  const tot = Q.reduce((n, k) => n + w(k), 0);
  let best = 0;
  for (const P of e._k.phrases) { // the best single phrasing: question EN, question HE, or a learned alias
    let hit = 0;
    for (const k of Q) { if (P.has(k)) hit += w(k); else if (e._k.steps.has(k)) hit += w(k) * 0.45; }
    const prec = Q.filter(k => P.has(k)).length / Math.max(3, P.size);
    best = Math.max(best, (hit / tot) * 0.8 + prec * 0.35);
  }
  return Math.min(1, best);
}

// audiences: e.g. ['parent'] or ['specialist','clinic-head']
// exclude: an optional Set of library codes to leave out (the videos this parent already said did not help, this panel session).
export function search(index, query, audiences, limit = 3, exclude = null) {
  return index.library
    .filter(e => audiences.includes(e.audience) && !(exclude && exclude.has(e.code)))
    .map(e => ({ e, s: score(index, query, e) }))
    .filter(r => r.s > 0.18)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit);
}

// A strong match opens straight away; otherwise "Did you mean…?".
export function isStrong(results) {
  return results.length > 0 && results[0].s >= 0.62 && (!results[1] || results[0].s - results[1].s > 0.08);
}
